import React, { useState, useContext } from 'react';
import axios from 'axios';
import { User, Mail, Phone, Lock, Eye, EyeOff, ArrowLeft, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { AnimatePresence, motion } from 'framer-motion';
import VerifyModal from './VerifyModal';

export default function GydeSignUp() {
  const navigate = useNavigate();

  // Access setUser from UserContext
  const { setUser } = useUser();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    dob: '',
    password: '',
    confirmPassword: ''
  });
  const [countryCode, setCountryCode] = useState('+234');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  // Country code to max phone length map
  const phoneMaxLengthMap = {
    '+234': 10, // Nigeria
    '+1': 10,   // USA
    '+44': 10,  // UK (varies, but 10 for mobile)
    '+91': 10,  // India
    '+27': 9,   // South Africa
    '+233': 9,  // Ghana
    '+225': 8,  // Ivory Coast
    '+250': 9,  // Rwanda
    '+254': 9,  // Kenya
    '+49': 11,  // Germany (varies)
    '+33': 9,   // France
    '+61': 9,   // Australia
    '+81': 10,  // Japan
    '+351': 9,  // Portugal
  };
  const phoneMaxLength = phoneMaxLengthMap[countryCode] || 10;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'phone') {
      // Only allow numbers
      newValue = value.replace(/\D/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Combine country code and phone for submission
  const getFullPhone = () => {
    // Remove leading zeros from phone
    const phone = formData.phone.replace(/^0+/, '');
    return countryCode + phone;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    const { name, email, gender, dob, password, confirmPassword } = formData;
    const phone = getFullPhone();

    setPhoneError('');
    if (!name || !email || !formData.phone || !gender || !dob || !password || !confirmPassword) {
      alert("All fields are required.");
      return;
    }
    // Phone length validation
    if (formData.phone.length !== phoneMaxLength) {
      setPhoneError(`Phone number must be exactly ${phoneMaxLength} digits for the selected country.`);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }


    // Password validation: at least 6 chars, must have number, letter, symbol
    let pwdError = '';
    if (password.length < 6) {
      pwdError = 'Password must be 6+ characters with a letter and a symbol.';
    } else if (/^\d+$/.test(password)) {
      pwdError = 'Password must include letters and symbols.';
    } else if (/^[a-zA-Z]+$/.test(password)) {
      pwdError = 'Password must include numbers and symbols.';
    } else if (/^[a-zA-Z0-9]+$/.test(password)) {
      pwdError = 'Password must include at least one symbol.';
    } else if (!/[a-zA-Z]/.test(password)) {
      pwdError = 'Password must include at least one letter.';
    } else if (!/[0-9]/.test(password)) {
      pwdError = 'Password must include at least one number.';
    } else if (!/[^a-zA-Z0-9]/.test(password)) {
      pwdError = 'Password must include at least one symbol.';
    }
    setPasswordError(pwdError);
    if (pwdError) {
      return;
    }

    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      // Use the same base URL for both requests if possible
      const apiBase = process.env.REACT_APP_API_URL || 'https://gyde-backend-wjh9.onrender.com';
      // Register user
      const signupRes = await axios.post('https://gyde-backend-wjh9.onrender.com/api/auth/signup', {
        name,
        email,
        phone,
        gender,
        dob,
        password
      });
      // Only send OTP if signup was successful
      if (signupRes.data && signupRes.data.success !== false) {
        // Update user context with the new user data (excluding password)
        setUser({
          name,
          email,
          phone,
          gender,
          dob,
          // You can add more fields if needed
        });
        const otpRes = await axios.post('https://gyde-backend-wjh9.onrender.com/api/auth/send-otp', { email });
        setShowVerify(true);
      } else {
        alert(signupRes.data?.msg || "Sign up failed. Please try again.");
      }
    } catch (error) {
      // If error is from backend, show backend message
      const msg = error.response?.data?.msg || error.message || "Sign up failed. Please try again.";
      alert(msg);
      // Only show OTP modal if error is not due to duplicate email or validation
      // (do not show modal if signup failed)
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    console.log('Google sign up clicked');
  };

  const handleAppleSignUp = () => {
    console.log('Apple sign up clicked');
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const handleVerifyClose = () => {
    setShowVerify(false);
    navigate('/'); // redirect to login after verification
  };

  return (
    <div className="relative">
      {/* Main content, hidden when showVerify is true */}
      {!showVerify && (
        <div className={`min-h-screen flex items-center justify-center p-4 relative transition-all duration-300`}
          style={
            window.innerWidth >= 768
              ? { backgroundImage: "url('/backgroundscribble.png')", backgroundSize: '130%', backgroundPosition: 'center' }
              : {}
          }
        >
          {/* Overlay for desktop only */}
          {window.innerWidth >= 768 && (
            <div className="absolute inset-0 bg-white opacity-60 z-0 pointer-events-none"></div>
          )}
          <div
            className="w-full max-w-md rounded-t-3xl shadow-2xl overflow-hidden block bg-white md:bg-white z-10"
            style={{}}
        >
          <div className="px-8 py-8">
            {/* Animated Back Button */}
            <motion.button
              onClick={handleBackToLogin}
              className="flex items-center text-gray-600 hover:text-gray-800 mb-8 transition-colors"
              whileTap={{ scale: 0.85, rotate: -10 }}
              whileHover={{ scale: 1.08 }}
              transition={{ type: 'spring', stiffness: 400, damping: 15 }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
            </motion.button>

            <h2 className="text-3xl font-bold text-slate-800 mb-2 text-center">Create Account</h2>
            <p className="text-center text-gray-500 mb-8">Join us and start Gyding today</p>

            <form className="space-y-5" onSubmit={handleSignUp}>
              <InputField icon={<User />} name="name" value={formData.name} onChange={handleInputChange} placeholder="Full Name" />
              <InputField icon={<Mail />} name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" />
              <div className="flex gap-2 items-center">
                <div className="relative">
                  <select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    className="pl-4 pr-8 py-4 bg-gray-100 border-0 rounded-xl text-gray-700 focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all duration-200 appearance-none h-full"
                    style={{ minWidth: 90 }}
                  >
                    <option value="+234">🇳🇬 +234</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+233">🇬🇭 +233</option>
                    <option value="+225">🇨🇮 +225</option>
                    <option value="+250">🇷🇼 +250</option>
                    <option value="+254">🇰🇪 +254</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+351">🇵🇹 +351</option>
                    <option value="+234">🇳🇬 +234</option>
                  </select>
                </div>
              <div className="flex-1">
                <InputField icon={<Phone />} name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="Phone Number" maxLength={phoneMaxLength} />
                {phoneError && (
                  <p className="text-xs text-red-600 mt-1 ml-2">{phoneError}</p>
                )}
              </div>
              </div>
              <div className="space-y-4">
                <div className="relative">
                  <label htmlFor="gender" className="block text-xs text-gray-500 mb-1 ml-2">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-4 bg-gray-100 border-0 rounded-xl text-gray-700 focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all duration-200 appearance-none"
                    required
                  >
                    <option value="" disabled>...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <span className="absolute left-4 top-10 text-gray-400 pointer-events-none">
                    <User className="h-5 w-5" />
                  </span>
                </div>
                <div className="relative">
                  <label htmlFor="dob" className="block text-xs text-gray-500 mb-1 ml-2">Date of Birth</label>
                  <input
                    id="dob"
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-4 bg-gray-100 border-0 rounded-xl text-gray-700 focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all duration-200"
                    required
                  />
                  <span className="absolute left-4 top-10 text-gray-400 pointer-events-none">
                    <Calendar className="h-5 w-5" />
                  </span>
                </div>
              </div>
              <div>
                <PasswordField
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  visible={showPassword}
                  toggle={() => setShowPassword(!showPassword)}
                />
                {passwordError && (
                  <p className="text-xs text-red-600 mt-1 ml-2">{passwordError}</p>
                )}
              </div>
              <PasswordField
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm Password"
                visible={showConfirmPassword}
                toggle={() => setShowConfirmPassword(!showConfirmPassword)}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2 text-lg"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing Up...
                  </div>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>

            <div className="my-8">
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="mx-4 bg-white px-3 text-gray-500 text-sm">Or sign up with</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleGoogleSignUp}
                  className="flex-1 flex items-center justify-center py-3 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors gap-2 text-base font-medium"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>
                <button
                  onClick={handleAppleSignUp}
                  className="flex-1 flex items-center justify-center py-3 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors gap-2 text-base font-medium"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Apple
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-6">
                By creating an account, you agree to our <a href="#" className="text-blue-600 underline">Terms of Service</a> and{' '}
                <a
                  href="https://www.freeprivacypolicy.com/live/7f187260-3249-4862-b6a8-d7a4d87185f6"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </p>
            </div>

            <p className="text-center text-gray-500 mt-8">
              Already have an account?{' '}
              <a href="/login" className="text-blue-600 font-medium hover:underline">Sign In</a>
            </p>
          </div>
        </div>
        </div>
      )}
      {/* OTP Modal, shown when showVerify is true */}
      <AnimatePresence>
        {showVerify && (
          <VerifyModal email={formData.email} onClose={handleVerifyClose} />
        )}
      </AnimatePresence>
    </div>
  );
}


// Reusable Input Field Component
const InputField = ({ icon, name, value, onChange, placeholder, type = 'text' }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      {icon}
    </div>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full pl-12 pr-4 py-4 bg-gray-100 border-0 rounded-xl text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all duration-200"
      required
    />
  </div>
);

// Exported OTP verification function for use in OTP modal
export const verifyOTP = async (email, otp) => {
  try {
    const response = await axios.post('https://gyde-backend-wjh9.onrender.com/api/auth/verify-otp', {
      email,
      otp,
    });
    console.log('OTP verified:', response.data);
    // Proceed to logged-in dashboard
    return response.data;
  } catch (error) {
    console.error('OTP verification failed:', error.response?.data || error.message);
    throw error;
  }
};

// Reusable Password Field Component
const PasswordField = ({ name, value, onChange, placeholder, visible, toggle }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      <Lock className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type={visible ? "text" : "password"}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full pl-12 pr-12 py-4 bg-gray-100 border-0 rounded-xl text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all duration-200"
      required
    />
    <button
      type="button"
      onClick={toggle}
      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
    >
      {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  </div>
);
